import { useEffect, useCallback, useRef } from 'react';
import { useCookieConsent } from './useCookieConsent';
import { META_PIXEL_CONFIG } from '@/config/metaPixel';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

interface MetaPixelConfig {
  pixelId: string;
}

// Exported interface for trackFormSubmit parameters
export interface FormSubmitTrackingData {
  traffic_source?: string;
  traffic_medium?: string;
  traffic_campaign?: string;
  language?: string;
  device_type?: string;
  event_id?: string;
}

/**
 * Generate unique event ID for deduplication between browser and server events
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Detect device type for tracking (allowed parameter)
 */
function getDeviceType(): string {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) return 'mobile';
  if (/ipad|tablet|playbook|silk/i.test(ua)) return 'tablet';
  return 'desktop';
}

/**
 * Simple SHA256 hash function for PII data
 * Meta requires hashed PII for advanced matching
 */
async function hashSHA256(value: string): Promise<string> {
  if (!value || typeof window === 'undefined') return '';
  const normalized = value.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Sanitize parameters to remove any health-related or sensitive data
 * Meta Health & Wellness policy prohibits sending treatment/condition data
 */
function sanitizeParameters(params?: Record<string, any>): Record<string, any> {
  if (!params) return {};
  
  // List of restricted parameters that could indicate health conditions
  const restrictedKeys = [
    'treatment',
    'treatment_type',
    'medical_condition',
    'health_status',
    'diagnosis',
    'symptoms',
    'medication',
    'procedure',
    'dental_treatment',
    'health_info',
  ];
  
  // Health-related terms to sanitize from content_name and other string values
  const healthTerms = [
    'implant', 'crown', 'veneer', 'whitening', 'orthodontic', 'root canal', 
    'dental', 'hollywood smile', 'all-on-4', 'all-on-6', 'zirconium', 'porcelain',
    'teeth', 'tooth', 'smile', 'extraction', 'filling', 'cleaning'
  ];
  
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(params)) {
    // Skip restricted health-related keys
    if (restrictedKeys.includes(key.toLowerCase())) continue;
    
    // Sanitize string values containing health terms
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      
      // Check if value contains health-related terms
      if (healthTerms.some(term => lowerValue.includes(term))) {
        // For content_name, replace with generic values
        if (key === 'content_name' || key === 'content_category') {
          sanitized[key] = 'Service Page';
        } else {
          sanitized[key] = 'Consultation';
        }
        continue;
      }
    }
    
    sanitized[key] = value;
  }
  
  return sanitized;
}

/**
 * Lead values by service type for Meta optimization (in EUR)
 * Used to pass estimated conversion value to Meta for better ad optimization
 */
const LEAD_VALUES: Record<string, number> = {
  'dental-implants': 1500,
  'hollywood-smile': 2500,
  'veneers': 1800,
  'zirconium-crowns': 1200,
  'teeth-whitening': 300,
  'orthodontic': 800,
  'root-canal': 400,
  'all-on-4': 4000,
  'all-on-6': 5000,
  'general': 1000,
};

/**
 * Hook to manage Meta Pixel with GDPR/KVKK compliant consent handling
 * Configured for Health & Wellness - Other category
 * Complies with Meta's restricted data sharing requirements
 */
export const useMetaPixel = (config?: MetaPixelConfig) => {
  const { isAllowed, consentData } = useCookieConsent();
  const pixelId = config?.pixelId || META_PIXEL_CONFIG.pixelId || import.meta.env.VITE_META_PIXEL_ID || '';
  const initializedRef = useRef(false);

  // Initialize Meta Pixel with limited data usage
  const initPixel = useCallback(() => {
    if (!pixelId || typeof window === 'undefined') return;
    if (window.fbq || initializedRef.current) return; // Already initialized

    initializedRef.current = true;

    // Meta Pixel base code
    const fbq = function(...args: any[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    } as any;

    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;

    // Load the pixel script
    const existingScript = document.getElementById('meta-pixel-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.id = 'meta-pixel-script';
      document.head.appendChild(script);
    }

    // Initialize pixel with limited data use (LDU) for health category
    // This enables Limited Data Use mode for restricted verticals
    window.fbq('init', pixelId, {}, {
      agent: 'lovable-clinic',
    });
    
    // Enable Limited Data Use mode for EU/Turkey health compliance
    // This signals to Meta to restrict data processing for health verticals
    window.fbq('dataProcessingOptions', ['LDU'], 0, 0);
    
    // Track initial PageView with event ID for deduplication
    window.fbq('track', 'PageView', {}, { eventID: generateEventId() });
  }, [pixelId]);

  // Load pixel only when marketing consent is given
  useEffect(() => {
    if (!consentData) return;
    
    if (isAllowed('marketing') && pixelId) {
      initPixel();
    }
  }, [consentData, isAllowed, initPixel, pixelId]);

  // Track standard events with sanitized parameters and deduplication
  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (!isAllowed('marketing') || !window.fbq) return;
    
    const eventId = generateEventId();
    const sanitizedParams = sanitizeParameters(parameters);
    
    window.fbq('track', eventName, sanitizedParams, { eventID: eventId });
    
    return eventId; // Return for server-side deduplication
  }, [isAllowed]);

  // Track custom events with sanitized parameters
  const trackCustomEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (!isAllowed('marketing') || !window.fbq) return;
    
    const eventId = generateEventId();
    const sanitizedParams = sanitizeParameters(parameters);
    
    window.fbq('trackCustom', eventName, sanitizedParams, { eventID: eventId });
    
    return eventId;
  }, [isAllowed]);

  /**
   * Hash and set user data for advanced matching
   * Only allowed PII fields: em (email), ph (phone), fn, ln, ct, st, zp, country
   */
  const setUserData = useCallback(async (userData: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  }) => {
    if (!isAllowed('marketing') || !window.fbq) return;

    const hashedData: Record<string, string> = {};

    if (userData.email) {
      hashedData.em = await hashSHA256(userData.email);
    }
    if (userData.phone) {
      // Remove non-numeric characters and hash
      const cleanPhone = userData.phone.replace(/\D/g, '');
      hashedData.ph = await hashSHA256(cleanPhone);
    }
    if (userData.firstName) {
      hashedData.fn = await hashSHA256(userData.firstName);
    }
    if (userData.lastName) {
      hashedData.ln = await hashSHA256(userData.lastName);
    }
    if (userData.city) {
      hashedData.ct = await hashSHA256(userData.city);
    }
    if (userData.state) {
      hashedData.st = await hashSHA256(userData.state);
    }
    if (userData.zipCode) {
      hashedData.zp = await hashSHA256(userData.zipCode);
    }
    if (userData.country) {
      hashedData.country = await hashSHA256(userData.country);
    }

    // Only set if we have data
    if (Object.keys(hashedData).length > 0) {
      window.fbq('init', pixelId, hashedData);
    }
  }, [isAllowed, pixelId]);

  // Pre-defined conversion events (Meta compliant)
  const trackLead = useCallback((parameters?: { 
    content_name?: string; 
    content_category?: string; 
    value?: number; 
    currency?: string;
    traffic_source?: string;
    traffic_medium?: string;
    traffic_campaign?: string;
    device_type?: string;
    language?: string;
    lead_type?: string; // Used to determine estimated value
  }) => {
    // Calculate estimated lead value based on type
    const leadType = parameters?.lead_type || 'general';
    const estimatedValue = parameters?.value || LEAD_VALUES[leadType] || LEAD_VALUES.general;
    
    // Use generic content category, never health-specific
    return trackEvent('Lead', {
      content_name: parameters?.content_name || 'Consultation Request',
      content_category: 'Services', // Generic, not "Dental" or "Health"
      value: estimatedValue,
      currency: parameters?.currency || 'EUR',
      device_type: parameters?.device_type || getDeviceType(),
      language: parameters?.language,
      // Traffic attribution is allowed
      traffic_source: parameters?.traffic_source,
      traffic_medium: parameters?.traffic_medium,
      traffic_campaign: parameters?.traffic_campaign,
    });
  }, [trackEvent]);

  const trackViewContent = useCallback((parameters?: { 
    content_name?: string; 
    content_category?: string; 
    content_type?: string; 
    value?: number; 
    currency?: string;
    language?: string;
    // Enhanced parameters for better optimization
    traffic_source?: string;
    traffic_medium?: string;
    traffic_campaign?: string;
    page_path?: string;
    referrer?: string;
  }) => {
    return trackEvent('ViewContent', {
      content_name: parameters?.content_name || 'Page',
      content_category: parameters?.content_category || 'Services', // Generic category
      content_type: parameters?.content_type || 'page',
      device_type: getDeviceType(),
      language: parameters?.language,
      // Enhanced attribution
      traffic_source: parameters?.traffic_source,
      traffic_medium: parameters?.traffic_medium,
      traffic_campaign: parameters?.traffic_campaign,
      page_path: parameters?.page_path || (typeof window !== 'undefined' ? window.location.pathname : ''),
      referrer: parameters?.referrer || (typeof document !== 'undefined' ? document.referrer : ''),
    });
  }, [trackEvent]);

  const trackInitiateCheckout = useCallback((parameters?: { 
    content_name?: string; 
    value?: number; 
    currency?: string; 
    num_items?: number;
  }) => {
    return trackEvent('InitiateCheckout', {
      content_name: parameters?.content_name || 'Consultation',
      content_category: 'Services',
      value: parameters?.value,
      currency: parameters?.currency || 'EUR',
      num_items: parameters?.num_items,
    });
  }, [trackEvent]);

  // Contact event for phone/call clicks
  const trackContact = useCallback((parameters?: {
    content_name?: string;
    language?: string;
    device_type?: string;
  }) => {
    return trackEvent('Contact', {
      content_name: parameters?.content_name || 'Contact Request',
      content_category: 'Contact',
      device_type: parameters?.device_type || getDeviceType(),
      language: parameters?.language,
    });
  }, [trackEvent]);

  // Custom events - sanitized for health compliance
  const trackWhatsAppClick = useCallback((contentName?: string, language?: string) => {
    console.log('[Meta Pixel] Tracking WhatsApp click');
    
    // Fire standard Contact event (shows up in main events)
    trackEvent('Contact', { 
      content_name: 'WhatsApp Contact', 
      content_category: 'Contact',
      language: language || 'en',
      device_type: getDeviceType(),
    });
    
    // Also fire custom event for detailed tracking
    return trackCustomEvent('WhatsAppClick', { 
      content_name: 'Contact', // Generic, not treatment-specific
      content_category: 'Contact',
      language: language || 'en',
      device_type: getDeviceType(),
    });
  }, [trackEvent, trackCustomEvent]);

  const trackCallClick = useCallback((contentName?: string, language?: string) => {
    return trackCustomEvent('CallClick', { 
      content_name: 'Contact',
      content_category: 'Contact',
      language: language || 'en',
      device_type: getDeviceType(),
    });
  }, [trackCustomEvent]);

  const trackFormStart = useCallback((formType: string, language?: string) => {
    // Fire InitiateCheckout standard event (Meta optimizes for this)
    trackEvent('InitiateCheckout', {
      content_name: 'Consultation Form',
      content_category: 'Services',
      currency: 'EUR',
      num_items: 1,
      device_type: getDeviceType(),
      language: language || 'en',
    });
    
    // Also fire custom InitiateLead for detailed tracking
    return trackCustomEvent('InitiateLead', { 
      content_name: 'Form Started', // Generic
      content_category: 'Form',
      language: language || 'en',
      device_type: getDeviceType(),
    });
  }, [trackEvent, trackCustomEvent]);

  const trackFormSubmit = useCallback((trackingData?: FormSubmitTrackingData & { lead_type?: string }) => {
    if (!isAllowed('marketing') || !window.fbq) return trackingData?.event_id;
    
    // Use provided event_id or generate a new one
    const eventId = trackingData?.event_id || generateEventId();
    
    // Calculate estimated lead value based on type
    const leadType = trackingData?.lead_type || 'general';
    const estimatedValue = LEAD_VALUES[leadType] || LEAD_VALUES.general;
    
    const params = sanitizeParameters({
      content_name: 'Consultation Request',
      content_category: 'Services',
      value: estimatedValue,
      currency: 'EUR',
      traffic_source: trackingData?.traffic_source,
      traffic_medium: trackingData?.traffic_medium,
      traffic_campaign: trackingData?.traffic_campaign,
      language: trackingData?.language,
      device_type: trackingData?.device_type || getDeviceType(),
    });
    
    window.fbq('track', 'Lead', params, { eventID: eventId });
    
    return eventId; // Return for server-side deduplication
  }, [isAllowed]);

  return {
    isEnabled: isAllowed('marketing') && !!pixelId,
    trackEvent,
    trackCustomEvent,
    trackLead,
    trackViewContent,
    trackInitiateCheckout,
    trackContact,
    trackWhatsAppClick,
    trackCallClick,
    trackFormStart,
    trackFormSubmit,
    setUserData,
    generateEventId,
  };
};

export default useMetaPixel;
